import React, { useState, useContext, useRef, useEffect } from "react";
import styled from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation, useNavigate } from "react-router-dom";
import { DirectionContext } from "../App";
import API_ENDPOINTS from "../config/api";

const CheckStatusPage = () => {
  const { navigateTo } = useContext(DirectionContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const [accessCode, setAccessCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationFailed, setVerificationFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const phoneInputRef = useRef(null);

  // 쿼리 파라미터에서 displayAccessCode 읽어서 자동 입력 후 URL에서 제거
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const displayAccessCode = searchParams.get("displayAccessCode");
    if (displayAccessCode) {
      setAccessCode(displayAccessCode);
      // URL에서 displayAccessCode 파라미터 제거
      searchParams.delete("displayAccessCode");
      const newSearch = searchParams.toString();
      const newUrl = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      navigate(newUrl, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  // 토스트 메시지 표시 함수
  const showToastMessage = (message) => {
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => {
      setShowToast(false);
    }, 3000); // 3초 후 토스트 숨김
  };

  // 토스트 애니메이션
  const toastVariants = {
    initial: {
      opacity: 0,
      y: 50,
    },
    animate: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
      },
    },
    exit: {
      opacity: 0,
      y: 50,
      transition: {
        duration: 0.3,
      },
    },
  };

  // 애니메이션 변수
  const pageVariants = {
    initial: {
      opacity: 0,
    },
    in: {
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
    out: {
      opacity: 0,
      transition: {
        duration: 0.3,
      },
    },
  };

  const contentVariants = {
    initial: {
      opacity: 0,
      y: 20,
    },
    in: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.2,
        staggerChildren: 0.1,
      },
    },
  };

  // 핸드폰 번호 변경 처리
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 11);
    setPhoneNumber(value);

    // 11자리 입력 시 키보드 닫기
    if (value.length === 11 && phoneInputRef.current) {
      phoneInputRef.current.blur();
    }
  };

  // 접근 코드 검증 처리
  const handleVerifyAccess = async () => {
    if (phoneNumber.length !== 11 || accessCode.length === 0) {
      return;
    }

    setIsVerifying(true);
    setVerificationFailed(false);
    setErrorMessage("");

    try {
      const response = await fetch(
        `${API_ENDPOINTS.BASE_URL}/applications/verify-access`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phoneNumber: phoneNumber,
            accessCode: accessCode,
            lawsuitId: 3,
          }),
        },
      );

      // 404 에러 처리 (올바른 비밀번호가 아닌 경우)
      if (response.status === 404) {
        setVerificationFailed(true);
        showToastMessage("올바른 휴대폰 번호와 비밀번호를 입력해주세요.");
        setIsVerifying(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "정보 확인에 실패했습니다.");
      }

      const data = await response.json();

      // 응답 데이터를 세션 스토리지에 저장
      sessionStorage.setItem("applicationData", JSON.stringify(data));

      // 결제 완료된 경우에만 상태 페이지로 이동
      if (data.status === "PAYMENT_COMPLETED") {
        navigateTo("/status");
      } else {
        // 결제가 완료되지 않은 경우 모달 표시
        setModalType("incomplete");
        setShowModal(true);
      }
    } catch (error) {
      setVerificationFailed(true);
      setErrorMessage(error.message || "정보 확인에 실패했습니다.");
      showToastMessage(error.message || "정보 확인에 실패했습니다.");
    } finally {
      setIsVerifying(false);
    }
  };

  // 모달 확인 버튼
  const handleModalConfirm = () => {
    setShowModal(false);

    if (modalType === "incomplete") {
      // 결제가 완료되지 않은 경우 메인페이지로 이동
      navigateTo("/select");
    }
  };

  return (
    <PageBackground>
      <Container
        variants={pageVariants}
        initial="initial"
        animate="in"
        exit="out"
      >
        <Content variants={contentVariants} initial="initial" animate="in">
          {/* 토스트 알림 */}
          <AnimatePresence>
            {showToast && (
              <Toast
                variants={toastVariants}
                initial="initial"
                animate="animate"
                exit="exit"
              >
                <ToastIcon>!</ToastIcon>
                {toastMessage}
              </Toast>
            )}
          </AnimatePresence>

          <Header>
            <Title>접수내역 조회하기</Title>
            <Description>
              신청 완료 시 카카오 알림톡으로 받은 조회 비밀번호를 입력해주세요
            </Description>
          </Header>

          <FormContainer>
            <FormGroup>
              <Label>휴대폰 번호를 입력해주세요</Label>
              <PhoneInputContainer>
                <PhoneInput
                  type="tel"
                  placeholder="-없이 숫자만 입력해주세요"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  ref={phoneInputRef}
                  isNumeric
                />
              </PhoneInputContainer>
            </FormGroup>

            <FormGroup>
              <Label>카카오톡으로 받은 비밀번호를 입력해주세요</Label>
              <PhoneInputContainer>
                <PhoneInput
                  type="text"
                  placeholder="비밀번호를 입력해주세요"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value)}
                  maxLength={6}
                />
              </PhoneInputContainer>
              {verificationFailed && (
                <VerificationFail>
                  {errorMessage ||
                    "입력하신 정보로 신청 내역을 찾을 수 없습니다."}
                </VerificationFail>
              )}
            </FormGroup>
          </FormContainer>

          <ButtonsContainer>
            <ActionButton
              onClick={handleVerifyAccess}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={
                phoneNumber.length !== 11 ||
                accessCode.length === 0 ||
                isVerifying
              }
            >
              {isVerifying ? "조회 중..." : "완료하기"}
            </ActionButton>
          </ButtonsContainer>

          {/* 모달 */}
          {showModal && (
            <ModalOverlay>
              <ModalContainer>
                <ModalContent>
                  {modalType === "incomplete" ? (
                    <ModalText>
                      신청이 진행 중이지만 아직 완료되지 않았습니다.
                      <br />
                      신청 완료 후 조회가 가능합니다.
                    </ModalText>
                  ) : (
                    <ModalText>
                      정보를 불러오는 중 오류가 발생했습니다.
                      <br />
                      다시 시도해주세요.
                    </ModalText>
                  )}
                  <ModalButton onClick={handleModalConfirm}>확인</ModalButton>
                </ModalContent>
              </ModalContainer>
            </ModalOverlay>
          )}
        </Content>
      </Container>
    </PageBackground>
  );
};

// 스타일 컴포넌트
const PageBackground = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 0;
  background-color: #f7f7f7;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;

  @media (min-width: 769px) {
    padding: 40px 20px 60px;
    background-color: #f7f7f7;
  }
`;

const Container = styled(motion.div)`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  background-color: #f7f7f7;

  @media (max-width: 768px) {
    min-height: auto;
    box-shadow: none;
    padding: 0 0 80px 0;
    max-width: 100%;
    margin: 0;
    border-radius: 0;
  }

  @media (min-width: 769px) {
    border-radius: 20px;
    min-height: auto;
    max-height: none;
    max-width: 470px;
    margin: 0 auto;
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
    padding: 20px;
  }
`;

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  text-align: left;
  width: 100%;
  position: relative;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 60px 20px 80px;
  }

  @media (min-width: 769px) {
    padding: 40px 30px 40px 30px;
  }
`;

const Header = styled.div`
  width: 100%;
  margin-bottom: 40px;
  color: #333;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const Title = styled.h1`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 12px;
  word-break: keep-all;
  color: #333;
  text-align: left;
  width: 100%;
`;

const Description = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 0;
  word-break: keep-all;
  color: #666;
  text-align: left;
  width: 100%;
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: 30px;
`;

const FormGroup = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0;
`;

const Label = styled.label`
  display: block;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 12px;
  color: #333;
`;

const PhoneInputContainer = styled.div`
  display: flex;
  width: 100%;
  position: relative;
  box-sizing: border-box;
  padding: 0;
`;

const PhoneInput = styled.input`
  flex: 1;
  height: 56px;
  border-radius: 8px;
  border: 1px solid #e5e5e5;
  padding: 0 16px;
  font-size: 16px;
  background-color: white;
  width: 100%;
  box-sizing: border-box;
  letter-spacing: ${(props) => (props.isNumeric ? "2px" : "normal")};

  &::placeholder {
    color: #999;
    letter-spacing: normal;
  }

  &:focus {
    outline: none;
    border-color: #e31c20;
  }
`;

const VerificationFail = styled.div`
  width: 100%;
  padding: 10px;
  margin-top: 10px;
  background-color: rgba(255, 76, 76, 0.1);
  color: #ff4c4c;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 40px;
  padding: 0;

  @media (min-width: 769px) {
    max-width: 470px;
  }
`;

const ActionButton = styled(motion.button)`
  display: flex;
  width: 100%;
  height: 56px;
  padding: 0px 14px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border-radius: 20px;
  background: ${(props) => (props.disabled ? "#E0898A" : "#E31C20")};
  color: ${(props) => (props.disabled ? "#DDC2C2" : "white")};
  font-size: 16px;
  font-weight: 600;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border: none;
  transition: background-color 0.2s ease;
`;

// 모달 관련 스타일
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  touch-action: none;
`;

const ModalContainer = styled.div`
  width: 90%;
  max-width: 355px;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
`;

const ModalContent = styled.div`
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const ModalText = styled.p`
  font-size: 16px;
  color: #333;
  text-align: left;
  margin-bottom: 30px;
  line-height: 1.5;
  width: 100%;
`;

const ModalButton = styled.button`
  width: 100%;
  height: 50px;
  background-color: #e31c20;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  align-self: center;
`;

// 토스트 알림 스타일
const Toast = styled(motion.div)`
  position: fixed;
  bottom: 30px;
  left: 7%;
  /* 데스크탑에서는 32%로 위치 조정 */
  @media (min-width: 768px) {
    left: 38.5%;
    max-width: 400px;
    min-width: 40%;
  }

  transform: translateX(-50%);
  background-color: rgba(46, 56, 64, 0.9);
  color: white;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  z-index: 1000;
  min-width: 300px;
  max-width: 90%;
  text-align: left;
`;

const ToastIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background-color: #ff4c4c;
  color: white;
  border-radius: 50%;
  margin-right: 12px;
  font-size: 16px;
  font-weight: bold;
`;

export default CheckStatusPage;
